<template>
  <v-container class="white">
    <h1 class="display-2 text-center">Settings</h1>
    <h2>Profile Picture</h2>
    <div class="underline" />
    <v-form class="input" @submit.prevent="uploadPic">
      <v-file-input v-model="picture.file" accept=".png, .jpg, .jpeg, .gif" label="Image">
        Select a picture
      </v-file-input>
      <v-btn
        type="submit"
        color="accent"
        :disabled="picture.uploadPending || !picture.file"
        :loading="picture.uploadPending"
      >
        Upload
      </v-btn>
    </v-form>

    <h2>Name</h2>
    <div class="underline" />
    <v-form class="input" @submit.prevent="saveName">
      <v-text-field v-model="name.first" name="firstName" label="First Name" :rules="[rules.name_max]" />
      <v-text-field v-model="name.last" name="lastName" label="Last Name" :rules="[rules.name_max]" />
      <v-btn type="submit" color="accent"> Save </v-btn>
    </v-form>

    <h2>Change Password</h2>
    <div class="underline" />
    <v-form
      ref="newPasswordForm"
      v-model="changePassword.valid"
      autocomplete="off"
      class="input"
      @submit.prevent="savePassword"
    >
      <v-text-field
        v-model="changePassword.oldPassword"
        type="password"
        name="oldPassword"
        label="Old Password"
        :rules="[rules.required]"
      />
      <v-text-field
        v-model="changePassword.newPassword"
        type="password"
        name="newPassword"
        label="New Password"
        :rules="[rules.required, rules.password_min, rules.password_max]"
        @input="validateConfirmation"
        @focusout="resetIfEmpty"
      />
      <v-text-field
        v-model="changePassword.confirm"
        type="password"
        name="confirmPassword"
        label="Confirm Password"
        :rules="[rules.required, rules.passwordMatches]"
        @focusout="resetIfEmpty"
      />
      <v-btn type="submit" :disabled="!changePassword.valid" color="accent"> Save </v-btn>
    </v-form>

    <h2>Import Data</h2>
    <div class="underline" />
    <router-link :to="{ name: 'import-data' }" class="input"> Import From Letterboxd </router-link>

    <h2>Delete Account</h2>
    <div class="underline" />
    <v-btn @click="deleteDialog = true" color="red" text class="text-none"> Permanently Delete Account </v-btn>

    <v-dialog v-model="deleteDialog" max-width="500">
      <delete-account-dialog v-on:close="deleteDialog = false"></delete-account-dialog>
    </v-dialog>

    <v-bottom-sheet v-model="picture.uploadSuccess" inset overlay-opacity="0">
      <v-sheet class="text-center success" height="100px">
        <div class="uploadMessage">Upload Successful!</div>
      </v-sheet>
    </v-bottom-sheet>

    <snackbar />
  </v-container>
</template>

<script>
import Snackbar from "../components/snackbar.vue";
import DeleteAccountDialog from "../components/delete-account-dialog.vue";
import { mapMutations } from "vuex";

export default {
  name: "SettingsPage",
  components: { Snackbar, DeleteAccountDialog },
  data: function () {
    return {
      rules: {
        required: (val) => val.length > 0 || "Required",
        passwordMatches: (val) => val === this.changePassword.newPassword || "Passwords don't match",
        password_min: (val) => val.length >= 8 || "Password Too Short - Must be 8+ chars",
        password_max: (val) => val.length <= 129 || "Password too long - 128 chars max",
        name_max: (val) => val.length <= 255 || "Name is too long - 255 characters max",
      },
      name: {
        first: "",
        last: "",
      },
      changePassword: {
        valid: false,
        oldPassword: "",
        newPassword: "",
        confirm: "",
      },
      picture: {
        file: undefined,
        uploadPending: false,
      },
      deleteDialog: false,
    };
  },
  mounted() {
    this.populateForms();
  },
  methods: {
    ...mapMutations(["showSnackbar"]),

    validateConfirmation: function () {
      if (this.changePassword.confirm !== "") {
        this.$refs.newPasswordForm.validate();
      }
    },

    resetIfEmpty: function () {
      if (this.changePassword.newPassword === "" && this.changePassword.confirm === "") {
        this.$refs.newPasswordForm.reset();
      }
    },

    populateForms: function () {
      this.$axios
        .get("/profile", {
          params: {
            user: this.$store.state.username,
          },
        })
        .then((response) => {
          console.log(response);
          this.name.first = response.data.first_name;
          this.name.last = response.data.last_name;
        });
    },
    savePassword: function () {
      if (this.$refs.newPasswordForm.validate()) {
        this.$axios
          .post("/password", {
            old_password: this.changePassword.oldPassword,
            new_password: this.changePassword.newPassword,
          })
          .then(() => {
            console.log("Password updated");
            this.showSnackbar({
              text: "Password Updated",
            });
          })
          .catch((err) => {
            this.showSnackbar({ text: "An error occurred", error: true });
            console.log(err);
          });
      }
    },

    uploadPic: function () {
      this.picture.uploadPending = true;
      let formData = new FormData();
      formData.append("file", this.picture.file);

      this.$axios
        .post("/profile-pic", formData, {
          headers: {
            "Content-Type": `multipart/form-data`,
          },
        })
        .then((response) => {
          console.log(response);
          this.showSnackbar({
            text: "Upload Successful",
          });
          this.picture.uploadPending = false;
        })
        .catch((err) => {
          let errorMsg = err.response.data.message;
          this.showSnackbar({
            text: "An error occurred" + (errorMsg ? ": " + errorMsg : ""),
            timeout: 6000,
          });
          this.picture.uploadPending = false;
        });
    },

    saveName: function () {
      this.$axios
        .post("/name", {
          first_name: this.name.first,
          last_name: this.name.last,
        })
        .then(() => {
          console.log("name updated");
          this.showSnackbar({
            text: "Name Updated",
          });
        });
    },
  },
};
</script>
<style scoped>
.underline {
  height: 3px;
  background-color: var(--v-primary-base);
  border-radius: 5px;
  margin-bottom: 10px;
  width: 30%;
  min-width: 300px;
}
.input {
  display: block;
  margin-left: 20px;
  margin-bottom: 50px;
}
</style>
